import { useCallback, useEffect, useRef } from 'react';
import { focusEditable } from '../../../DocEditor/utils/focusUtils';
import { splitTitleOnEnter } from '../../titleEnter';
import { SopAPI } from '../../../../utils/api';
import { AUTOSAVE_DELAY } from '../constants';

/**
 * Title/icon persist. Mission-attached docs are ordinary docs — renaming
 * here just renames the doc; attachments reference the id and never break.
 */
export function useLibraryDocMeta({
  docId,
  setDoc,
  title,
  setTitle,
  blocks,
  handleBlocksChange,
  setSaveState,
}) {
  const titleTimerRef = useRef(null);

  const saveMeta = useCallback(
    async (fields) => {
      setSaveState('saving');
      try {
        await SopAPI.update(docId, fields);
        setSaveState('saved');
        window.dispatchEvent(new CustomEvent('fokus:docs-changed'));
      } catch {
        setSaveState('error');
      }
    },
    [docId, setSaveState]
  );

  const handleTitleChange = (e) => {
    const next = e.target.value;
    setTitle(next);
    clearTimeout(titleTimerRef.current);
    titleTimerRef.current = setTimeout(() => {
      saveMeta({ title: next.trim() || 'Untitled Doc' });
    }, AUTOSAVE_DELAY);
  };

  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const input = e.target;
      const next = splitTitleOnEnter(
        title,
        input.selectionStart,
        input.selectionEnd,
        blocks
      );
      setTitle(next.title);
      clearTimeout(titleTimerRef.current);
      titleTimerRef.current = setTimeout(() => {
        saveMeta({ title: next.title.trim() || 'Untitled Doc' });
      }, AUTOSAVE_DELAY);
      handleBlocksChange(next.blocks);
      focusEditable(next.focusBlockId, { atStart: true });
    }
    e.stopPropagation(); // sheet-level Esc/shortcut handlers stay out of typing
  };

  useEffect(() => () => clearTimeout(titleTimerRef.current), []);

  const handleIconChange = (newIcon) => {
    setDoc((prev) => (prev ? { ...prev, icon: newIcon } : prev));
    saveMeta({ icon: newIcon });
  };

  return { handleTitleChange, handleTitleKeyDown, handleIconChange };
}
